import React, { useState, useCallback, useMemo } from "react";
import { Input, Button, Form, Dropdown, message, DatePicker, Image, Select, AutoComplete } from "antd";
import type { MenuProps } from "antd";
import { RcFile } from "antd/es/upload/interface";
import { v4 as uuidv4 } from "uuid";
import dayjs from "dayjs";
import { ApolloError } from "@apollo/client";
import { core, usAutocompletePro } from "smartystreets-javascript-sdk";

import UploadPhotoModal from "components/upload-photo-modal/upload-photo-modal";
import AddAttachmentModal from "components/add-attachment-modal/add-attachment-modal";
import AttachmentItem from "components/attachment-item/attachment-item";

import { CreatePropertyInput, Property_Documents_Insert_Input, Users } from "graphql/generated/graphql";
import { useGetOrganizationsFilteredQuery } from "graphql/queries/get-organizations-filtered.generated";
import { useGetUsersByOrganizationIdsQuery } from "graphql/queries/get-users-by-organization-ids.generated";
import { useSelectedAttachments, clearSelectedAttachments } from "state/reactive-vars/selected-attachment-var";
import { LoggedUserType, useLoggedUser } from "state/reactive-vars/logged-user-var";
import { getFileExtension, uploadToStorage } from "utils/files-management";
import { UserRoleValue, AttachmentType, PropertyDocumentsType } from "constants/field-values";
import {
	BUILDING_TYPE_OPTIONS,
	OCCUPANCY_CLASS_OPTIONS,
	CONSTRUCTION_TYPE_OPTIONS,
	FIRE_SUPPRESSION_OPTIONS,
	NUMBER_OF_STORIES_OPTIONS,
	NUMBER_OF_UNITS_OPTIONS,
	YEAR_BUILT_OPTIONS
} from "constants/property-field-options";
import { SHOW_DRAWINGS_SECTION } from "constants/constants";
import { pruneEmptyValues } from "utils/helpers";

const smartyClient = new core.ClientBuilder(
	new core.SharedCredentials(process.env.REACT_APP_SMARTY_API_KEY || "")
).buildUsAutocompleteProClient();

export interface AddressSuggestion {
	value: string;
	street_1: string;
	street_2?: string;
	city: string;
	state: string;
	postal_code: string;
}

export async function fetchAddressSuggestions(searchText: string): Promise<AddressSuggestion[]> {
	const lookup = new usAutocompletePro.Lookup(searchText);
	lookup.maxResults = 10;
	await smartyClient.send(lookup);
	return lookup.result.map((s: InstanceType<typeof usAutocompletePro.Suggestion>) => ({
		value: s.secondary
			? `${s.streetLine} ${s.secondary}, ${s.city}, ${s.state} ${s.zipcode}`
			: `${s.streetLine}, ${s.city}, ${s.state} ${s.zipcode}`,
		street_1: s.streetLine,
		street_2: s.secondary || undefined,
		city: s.city,
		state: s.state,
		postal_code: s.zipcode
	}));
}

export interface PropertyFormOutput {
	// Text fields
	name: string;
	address: string;
	selectedAddressComponents?: {
		street_1: string;
		street_2?: string;
		city: string;
		state: string;
		postal_code: string;
	} | null;
	buildingType: string;
	occupancyClass: string;
	numberOfStories: string;
	numberOfUnits: string;
	constructionType: string;
	fireSuppression: string;
	yearBuilt: string;
	contactName: string;
	contactPhoneNumber: string;
	contactEmail: string;
	isPremium: boolean;
	expectedDeviceTypes: string[];
	warrantyDateStart: dayjs.Dayjs | null;
	warrantyDateEnd: dayjs.Dayjs | null;
	selectedUsers: Users[];
	organizationId: string;

	// Staged files (not yet uploaded to Firebase)
	propertyPhotoFile: RcFile | null;
	existingPhotoUrl: string;
	drawingsFiles: any[];
	// Floor-plan files (PDF/image). Uploaded as property_documents with type
	// FLOOR_PLAN; feed the Ollie room-hierarchy extraction after the property is saved.
	floorPlanFiles: any[];
	documentsFiles: any[];
	warrantyDocFile: any | null;
}

export interface PropertyFormProps {
	/**
	 * Pre-filled values — used to restore a draft (e.g. when re-opening the modal
	 * after the user previously filled it in and clicked the pencil to edit again).
	 */
	initialValues?: Partial<PropertyFormOutput>;

	/** Label for the primary action button. @default "Save" */
	submitLabel?: string;

	/**
	 * Called when the user clicks the submit button.
	 * Receives the raw form output — NO Firebase uploads have happened yet.
	 * The caller decides when and how to upload.
	 */
	onSubmit: (output: PropertyFormOutput) => void;

	/** Disables the form while an external operation (e.g. a mutation) is in progress. */
	externalLoading?: boolean;

	/**
	 * Optional slot rendered to the left of the submit button.
	 * Useful for a Cancel button when rendered inside a modal.
	 */
	renderExtraActions?: () => React.ReactNode;

	/**
	 * Additional organizations to inject into the org selector — used when a new
	 * organization has been staged in the parent (e.g. HandoverAdd) but not yet
	 * persisted. These appear at the top of the list before query results.
	 */
	extraOrganizations?: Array<{ id: string; name: string }>;

	/**
	 * When provided, the org selector is pre-set to this id and disabled.
	 * Used by HandoverAdd to lock the org to the newly-invited customer.
	 */
	lockedOrganizationId?: string;

	/**
	 * Whether this is being used in the handover flow
	 * If not, filter out organizations that don't have a published handover
	 */
	isHandoverForm?: boolean;
}

/**
 * Uploads all staged files in a `PropertyFormOutput` to Firebase Storage and
 * returns a fully-built `CreatePropertyInput` ready for the GraphQL mutation.
 *
 * Call this inside the handler that fires the final mutation (onFinish / handleOnSubmit),
 * NOT inside PropertyForm itself.
 */
export async function uploadPropertyFormData(
	output: PropertyFormOutput,
	loggedUser: LoggedUserType
): Promise<CreatePropertyInput> {
	const {
		name,
		address,
		buildingType,
		occupancyClass,
		numberOfStories,
		numberOfUnits,
		constructionType,
		fireSuppression,
		yearBuilt,
		contactName,
		contactPhoneNumber,
		contactEmail,
		isPremium,
		expectedDeviceTypes,
		warrantyDateStart,
		warrantyDateEnd,
		selectedUsers,
		organizationId,
		propertyPhotoFile,
		existingPhotoUrl,
		drawingsFiles,
		floorPlanFiles,
		documentsFiles,
		warrantyDocFile,
		selectedAddressComponents
	} = output;

	// 1. Upload property photo (only if a new file was staged)
	let photoUrl = existingPhotoUrl;
	if (propertyPhotoFile) {
		try {
			const uniqueID = uuidv4();
			const bucketName = "property-photos/";
			const fileName = `property-photo-${name}-${uniqueID}-${Math.floor(Date.now() / 1000)}-${propertyPhotoFile.name}`;
			const { publicUrl } = await uploadToStorage(propertyPhotoFile, () => {}, `${bucketName}/${fileName}`);
			photoUrl = publicUrl;
		} catch (error) {
			console.error("Error uploading property photo:", error);
			message.error("Failed to upload property photo.");
		}
	}

	// 2. Upload documents (resources + warranty)
	const drawingsWithType = drawingsFiles.map(f => ({ ...f, type: f.type || PropertyDocumentsType.DRAWING }));
	// Floor plans are stored as property_documents with the FLOOR_PLAN type so the
	// backend extraction can find them.
	const floorPlansWithType = floorPlanFiles.map(f => ({ ...f, type: PropertyDocumentsType.FLOOR_PLAN }));
	const allDocFiles = [
		...(warrantyDocFile ? [warrantyDocFile] : []),
		...documentsFiles,
		...drawingsWithType,
		...floorPlansWithType
	];

	let uploadedDocuments: Property_Documents_Insert_Input[] = [];

	if (allDocFiles.length > 0) {
		const promises = allDocFiles.map(document => {
			const { type, description, file, name: docName } = document;
			const fileMediaType = file.type.split("/")[0];
			const uniqueID = uuidv4();
			const fileExtension = getFileExtension(file.name);
			const fileName = docName ? `${docName}.${fileExtension}` : file.name;

			let bucketName = "property-documents/";
			let attachmentName = "";

			const newDocument: Property_Documents_Insert_Input = {
				photo_link: null,
				document_link: null,
				video_link: null,
				audio_link: null,
				type,
				description,
				name: fileName
			};

			switch (fileMediaType) {
				case "image":
					bucketName += "photos";
					attachmentName = `property-document-photo-${uniqueID}-${Math.floor(Date.now() / 1000)}-${fileName}`;
					break;
				case "video":
					bucketName += "videos";
					attachmentName = `property-document-video-${uniqueID}-${Math.floor(Date.now() / 1000)}-${fileName}`;
					break;
				case "audio":
					bucketName += "audios";
					attachmentName = `property-document-audio-${uniqueID}-${Math.floor(Date.now() / 1000)}-${fileName}`;
					break;
				default:
					bucketName += "documents";
					attachmentName = `property-document-${uniqueID}-${Math.floor(Date.now() / 1000)}-${fileName}`;
			}

			return (async () => {
				try {
					const fileObj = file?.originFileObj;
					const { publicUrl } = await uploadToStorage(fileObj, () => {}, `${bucketName}/${attachmentName}`);

					switch (fileMediaType) {
						case "image":
							newDocument.photo_link = publicUrl;
							break;
						case "video":
							newDocument.video_link = publicUrl;
							break;
						case "audio":
							newDocument.audio_link = publicUrl;
							break;
						default:
							newDocument.document_link = publicUrl;
					}
					return newDocument;
				} catch (err) {
					console.error("Error uploading document:", err);
				}
			})();
		});

		try {
			const results = await Promise.all(promises);
			uploadedDocuments = pruneEmptyValues(results) as Property_Documents_Insert_Input[];
		} catch (err) {
			console.error("Error uploading documents:", err);
			message.error("Failed to upload documents.");
		}
	}

	// 3. Build CreatePropertyInput
	const propertyInput: CreatePropertyInput = {
		name,
		address,
		premium: isPremium,
		organization_id: organizationId || loggedUser.organization_id,
		workspace_id: loggedUser.workspace_id
	};

	if (photoUrl) propertyInput.photo_link = photoUrl;
	if (buildingType) propertyInput.building_type = buildingType;
	if (occupancyClass) propertyInput.occupancy_class = occupancyClass;
	if (numberOfStories) propertyInput.num_stories = parseInt(numberOfStories, 10);
	if (numberOfUnits) propertyInput.num_units = parseInt(numberOfUnits, 10);
	if (constructionType) propertyInput.construction_type = constructionType;
	if (fireSuppression) propertyInput.sprinklered = fireSuppression;
	if (yearBuilt) propertyInput.year_built = parseInt(yearBuilt, 10);
	if (warrantyDateStart) propertyInput.warranty_start_date = warrantyDateStart.format("YYYY-MM-DDTHH:mm:ssZ");
	if (warrantyDateEnd) propertyInput.warranty_expiration_date = warrantyDateEnd.format("YYYY-MM-DDTHH:mm:ssZ");
	if (uploadedDocuments.length > 0) propertyInput.attachments = uploadedDocuments;
	if (expectedDeviceTypes.length > 0) propertyInput.expected_device_type = expectedDeviceTypes;

	if (contactName || contactPhoneNumber || contactEmail) {
		propertyInput.contacts = [
			{ name: contactName || null, number: contactPhoneNumber || null, email: contactEmail || null }
		];
	}

	const propertyUsersIds: string[] = [loggedUser.id];
	selectedUsers.forEach(u => {
		if (u.id !== loggedUser.id) propertyUsersIds.push(u.id);
	});
	if (propertyUsersIds.length > 0) propertyInput.property_users_ids = propertyUsersIds;

	if (selectedAddressComponents) {
		propertyInput.standardized_address = {
			street_1: selectedAddressComponents.street_1,
			street_2: selectedAddressComponents.street_2 || null,
			city: selectedAddressComponents.city,
			state: selectedAddressComponents.state,
			postal_code: selectedAddressComponents.postal_code,
			country: "US"
		};
	}

	return propertyInput;
}

const PropertyForm: React.FC<PropertyFormProps> = ({
	initialValues = {},
	submitLabel = "Save",
	externalLoading = false,
	onSubmit,
	renderExtraActions,
	extraOrganizations = [],
	lockedOrganizationId,
	isHandoverForm = false
}) => {
	const { updateSelectedAttachments } = useSelectedAttachments();
	const { loggedUserValue } = useLoggedUser();

	const [selectedOrganizationId, setSelectedOrganizationId] = useState<string>(
		lockedOrganizationId || initialValues.organizationId || ""
	);
	const [name, setName] = useState<string>(initialValues.name || "");
	const [address, setAddress] = useState<string>(initialValues.address || "");
	const [addressOptions, setAddressOptions] = useState<{ value: string }[]>([]);
	const [addressSuggestions, setAddressSuggestions] = useState<
		{ value: string; street_1: string; street_2?: string; city: string; state: string; postal_code: string }[]
	>([]);
	const [selectedAddressComponents, setSelectedAddressComponents] = useState<{
		street_1: string;
		street_2?: string;
		city: string;
		state: string;
		postal_code: string;
	} | null>(null);
	const [buildingType, setBuildingType] = useState<string>(initialValues.buildingType || "");
	const [occupancyClass, setOccupancyClass] = useState<string>(initialValues.occupancyClass || "");
	const [numberOfStories, setNumberOfStories] = useState<string>(initialValues.numberOfStories || "");
	const [numberOfUnits, setNumberOfUnits] = useState<string>(initialValues.numberOfUnits || "");
	const [constructionType, setConstructionType] = useState<string>(initialValues.constructionType || "");
	const [fireSuppression, setFireSuppression] = useState<string>(initialValues.fireSuppression || "");
	const [yearBuilt, setYearBuilt] = useState<string>(initialValues.yearBuilt || "");
	const [contactName, setContactName] = useState<string>(initialValues.contactName || "");
	const [contactPhoneNumber, setContactPhoneNumber] = useState<string>(initialValues.contactPhoneNumber || "");
	const [contactEmail, setContactEmail] = useState<string>(initialValues.contactEmail || "");
	const [isPremium, setIsPremium] = useState<boolean>(initialValues.isPremium || false);
	const [expectedDeviceTypes, setExpectedDeviceTypes] = useState<string[]>(initialValues.expectedDeviceTypes || []);
	const [warrantyDateStart, setWarrantyDateStart] = useState<dayjs.Dayjs | null>(
		initialValues.warrantyDateStart || null
	);
	const [warrantyDateEnd, setWarrantyDateEnd] = useState<dayjs.Dayjs | null>(initialValues.warrantyDateEnd || null);

	const [addPropertyPhotoModalOpen, setAddPropertyPhotoModalOpen] = useState<boolean>(false);
	// Thumbnail shown in the UI (may be a local blob URL or an existing remote URL)
	const [propertyPhotoThumbUrl, setPropertyPhotoThumbUrl] = useState<string>(initialValues.existingPhotoUrl || "");
	// The actual new file staged for upload (null = no new file selected)
	const [propertyPhotoFile, setPropertyPhotoFile] = useState<RcFile | null>(initialValues.propertyPhotoFile || null);

	const [addDocumentModalOpen, setAddDocumentModalOpen] = useState<boolean>(false);
	const [addDocumentModalEditMode, setAddDocumentModalEditMode] = useState<boolean>(false);
	const [drawingsModalOpen, setDrawingsModalOpen] = useState<boolean>(false);
	const [drawingsFiles, setDrawingsFiles] = useState<any[]>(initialValues.drawingsFiles || []);
	const [floorPlansModalOpen, setFloorPlansModalOpen] = useState<boolean>(false);
	const [floorPlanFiles, setFloorPlanFiles] = useState<any[]>(initialValues.floorPlanFiles || []);
	const [documentsFiles, setDocumentsFiles] = useState<any[]>(initialValues.documentsFiles || []);
	const [warrantyDocMode, setWarrantyDocMode] = useState<boolean>(false);
	const [warrantyDocFile, setWarrantyDocFile] = useState<any | null>(initialValues.warrantyDocFile || null);

	const [usersDropdownOpen, setUsersDropdownOpen] = useState<boolean>(false);
	const [selectedUsers, setSelectedUsers] = useState<Users[]>(initialValues.selectedUsers || []);
	const [selectedUsersKeys, setSelectedUsersKeys] = useState<string[]>(
		initialValues.selectedUsers?.map(u => u.id) || []
	);

	// ── Organisations query (role-gated org selector) ──────────────────────────
	const organizationsFilter = useMemo(
		() => ({
			workspace_id: { _eq: loggedUserValue.workspace_id },
			...(isHandoverForm
				? {}
				: {
						_or: [
							{ created_by_handover: { status: { _eq: "PUBLISHED" } } },
							{ created_by_handover_id: { _is_null: true } }
						]
					})
		}),
		[loggedUserValue.workspace_id, isHandoverForm]
	);

	const { data: organizationsData, loading: loadingOrganizations } = useGetOrganizationsFilteredQuery({
		variables: { where: organizationsFilter },
		errorPolicy: "none",
		fetchPolicy: "network-only",
		nextFetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onError: useCallback((err: ApolloError) => {
			console.error("getOrganizationsFiltered error:", err);
		}, [])
	});

	const availableOrganizations = useMemo(
		() => [
			...extraOrganizations,
			...(organizationsData?.organizations?.map(org => ({
				id: org.id,
				name: org.name
			})) || [])
		],
		[organizationsData, extraOrganizations]
	);

	const { data: usersByOrganizationData, loading: loadingUsers } = useGetUsersByOrganizationIdsQuery({
		variables: {
			organization_ids: [loggedUserValue.organization_id]
		},
		errorPolicy: "none",
		fetchPolicy: "network-only",
		nextFetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onError: useCallback((err: ApolloError) => {
			console.error("getUsersByOrganizationIds error:", err);
		}, [])
	});

	// Users shown in the "Property Users" multi-select dropdown.
	const availableUsers = useMemo(() => (usersByOrganizationData?.users as Users[]) || [], [usersByOrganizationData]);

	// Whether to show the "Property Organization" selector.
	const showOrganizationSelect = [
		UserRoleValue.admin_user,
		UserRoleValue.vendor_manager,
		UserRoleValue.vendor_employee
	].includes((loggedUserValue.role || "") as UserRoleValue);

	const isLoading = externalLoading || loadingOrganizations || loadingUsers;

	const handleAddressSearch = useCallback(async (searchText: string) => {
		if (!searchText || searchText.length < 3) {
			setAddressOptions([]);
			return;
		}
		try {
			const suggestions = await fetchAddressSuggestions(searchText);
			setAddressSuggestions(suggestions);
			setAddressOptions(suggestions.map(s => ({ value: s.value })));
		} catch (error) {
			console.error("Smarty address autocomplete failed", error);
			setAddressOptions([]);
		}
	}, []);

	const handleAddressSelect = useCallback(
		(value: string) => {
			const match = addressSuggestions.find(s => s.value === value);
			if (match) {
				setSelectedAddressComponents({
					street_1: match.street_1,
					street_2: match.street_2,
					city: match.city,
					state: match.state,
					postal_code: match.postal_code
				});
			}
		},
		[addressSuggestions]
	);

	const handleOnConfirmPhoto = useCallback((file: RcFile, url: string) => {
		setPropertyPhotoThumbUrl(url);
		setPropertyPhotoFile(file);
	}, []);

	const handleOnRemovePhoto = useCallback(() => {
		setPropertyPhotoThumbUrl("");
		setPropertyPhotoFile(null);
	}, []);

	const updateDrawings = useCallback((documentFile: any) => {
		const { uid } = documentFile;
		setDrawingsFiles(prev => {
			const filtered = prev.filter(el => el.uid !== uid);
			return filtered.concat([{ ...documentFile, type: PropertyDocumentsType.DRAWING }]);
		});
	}, []);

	const handleOnRemoveDrawing = useCallback((documentFile: any) => {
		setDrawingsFiles(prev => prev.filter(el => el.uid !== documentFile.uid));
	}, []);

	const updateFloorPlans = useCallback((documentFile: any) => {
		const { uid } = documentFile;
		setFloorPlanFiles(prev => {
			const filtered = prev.filter(el => el.uid !== uid);
			return filtered.concat([{ ...documentFile, type: PropertyDocumentsType.FLOOR_PLAN }]);
		});
	}, []);

	const handleOnRemoveFloorPlan = useCallback((documentFile: any) => {
		setFloorPlanFiles(prev => prev.filter(el => el.uid !== documentFile.uid));
	}, []);

	const updateAttachments = useCallback((documentFile: any, warrantyMode?: boolean) => {
		const { uid } = documentFile;
		if (warrantyMode) {
			setWarrantyDocFile(documentFile);
			return;
		}
		setDocumentsFiles(prev => {
			const filtered = prev.filter(el => el.uid !== uid);
			return filtered.concat([documentFile]);
		});
	}, []);

	const handleOnRemoveDocument = useCallback((documentFile: any) => {
		setDocumentsFiles(prev => prev.filter(el => el.uid !== documentFile.uid));
	}, []);

	const handleOnEditDocument = useCallback(
		(documentFile: any) => {
			updateSelectedAttachments(documentFile);
			setAddDocumentModalEditMode(true);
			setAddDocumentModalOpen(true);
		},
		[updateSelectedAttachments]
	);

	const handleOnCloseDocumentModal = useCallback(() => {
		setAddDocumentModalOpen(false);
		setAddDocumentModalEditMode(false);
		setWarrantyDocMode(false);
		clearSelectedAttachments();
	}, []);

	const handleOnSelectUsers = useCallback(
		(args: any) => {
			const { selectedKeys } = args;

			// Merge available + already-selected to handle users not in the current list
			const allKnownUsers = availableUsers.reduce(
				(acc: Users[], u) => (acc.find(x => x.id === u.id) ? acc : [...acc, u]),
				[...selectedUsers]
			);

			const newSelected = allKnownUsers.filter(u => selectedKeys.includes(u.id));
			setSelectedUsers(newSelected);
			setSelectedUsersKeys(selectedKeys);
		},
		[availableUsers, selectedUsers]
	);

	const handleOnRemoveUser = useCallback((userId: string) => {
		setSelectedUsers(prev => prev.filter(u => u.id !== userId));
		setSelectedUsersKeys(prev => prev.filter(id => id !== userId));
	}, []);

	const usersOptions = useMemo((): MenuProps["items"] => {
		return availableUsers.map((user, idx) => {
			const optionId = `user-dropdown-option-${idx}-${user.id}`;
			const isSelected = selectedUsersKeys.includes(user.id as string);
			return {
				key: `${user.id}`,
				icon: (
					<div>
						<input
							className="custom-checkbox"
							id={optionId}
							type="checkbox"
							onClick={e => e.stopPropagation()}
							checked={isSelected}
							onChange={() => undefined}
						/>
						<label htmlFor={optionId} className="custom-checkbox-label">
							{`${user.first_name} ${user.last_name}`}
						</label>
					</div>
				)
			};
		});
	}, [availableUsers, selectedUsersKeys]);

	// Submit: collect raw state, NO Firebase uploads
	const handleOnSubmit = useCallback(() => {
		const output: PropertyFormOutput = {
			name,
			address,
			selectedAddressComponents,
			buildingType,
			occupancyClass,
			numberOfStories,
			numberOfUnits,
			constructionType,
			fireSuppression,
			yearBuilt,
			contactName,
			contactPhoneNumber,
			contactEmail,
			isPremium,
			expectedDeviceTypes,
			warrantyDateStart,
			warrantyDateEnd,
			selectedUsers,
			organizationId: selectedOrganizationId,
			propertyPhotoFile,
			existingPhotoUrl: propertyPhotoThumbUrl,
			drawingsFiles,
			floorPlanFiles,
			documentsFiles,
			warrantyDocFile
		};
		onSubmit(output);
	}, [
		name,
		address,
		selectedAddressComponents,
		buildingType,
		occupancyClass,
		numberOfStories,
		numberOfUnits,
		constructionType,
		fireSuppression,
		yearBuilt,
		contactName,
		contactPhoneNumber,
		contactEmail,
		isPremium,
		expectedDeviceTypes,
		warrantyDateStart,
		warrantyDateEnd,
		selectedUsers,
		selectedOrganizationId,
		propertyPhotoFile,
		propertyPhotoThumbUrl,
		drawingsFiles,
		floorPlanFiles,
		documentsFiles,
		warrantyDocFile,
		onSubmit
	]);

	return (
		<div className="property-form-container">
			{/* Property Organization (admin / vendor only) */}
			{showOrganizationSelect && (
				<div className="add-product-form">
					<span className="property-form-section-title">{"Organization"}</span>
					<Form.Item className="login-form-item">
						<div className="property-form-field-with-description">
							<span className="property-form-field-label">{"Customer Organization"}</span>
							<Select
								value={lockedOrganizationId || selectedOrganizationId || undefined}
								className="add-product-input-select"
								onChange={value => setSelectedOrganizationId(value)}
								options={availableOrganizations.map(org => ({
									value: org.id,
									label: org.name
								}))}
								placeholder="Select Organization"
								allowClear
								disabled={isLoading || !!lockedOrganizationId}
								suffixIcon={
									<img
										src={process.env.PUBLIC_URL + "/assets/images/icons/black-chevron-down.svg"}
										alt="dropdown icon"
										className="chevron-button"
									/>
								}
							/>
						</div>
					</Form.Item>
				</div>
			)}

			{/* Property Details */}
			<Form className="add-product-form property-form" autoComplete="off">
				<span className="property-form-section-title">{"Property Details"}</span>

				<Form.Item className="login-form-item">
					<div className="property-form-field-with-description">
						<span className="property-form-field-label">{"Property Name"}</span>
						<Input
							name="name"
							placeholder="Property Name"
							className="add-product-input-pair"
							value={name}
							autoComplete="off"
							onChange={e => setName(e.target.value)}
							allowClear
							disabled={isLoading}
						/>
					</div>
				</Form.Item>

				<Form.Item className="login-form-item">
					<div className="property-form-field-with-description">
						<span className="property-form-field-label">{"Property Address"}</span>
						<AutoComplete
							options={addressOptions}
							onSearch={handleAddressSearch}
							onSelect={handleAddressSelect}
							onChange={(value: string) => {
								setAddress(value);
								if (!value) {
									setAddressOptions([]);
									setSelectedAddressComponents(null);
								}
							}}
							value={address}
							disabled={isLoading}
							className="add-product-input-pair"
							allowClear
						>
							<Input
								name="address"
								placeholder="Property Address"
								autoComplete="off"
								className="add-product-input-pair"
							/>
						</AutoComplete>
					</div>
				</Form.Item>

				<Form.Item className="login-form-item" validateTrigger="onBlur">
					<div className="property-form-field-with-description">
						<span className="property-form-field-label">{"Construction Type (optional)"}</span>
						<Select
							placeholder="Select a Construction Type"
							className="add-product-input-pair"
							value={constructionType || undefined}
							onChange={value => setConstructionType(value)}
							options={CONSTRUCTION_TYPE_OPTIONS}
							allowClear
							disabled={isLoading}
							suffixIcon={
								<img
									src={process.env.PUBLIC_URL + "/assets/images/icons/black-chevron-down.svg"}
									alt="dropdown icon"
									className="chevron-button"
								/>
							}
						/>
					</div>
				</Form.Item>

				<Form.Item className="login-form-item" validateTrigger="onBlur">
					<div className="property-form-field-with-description">
						<span className="property-form-field-label">{"Property Type (optional)"}</span>
						<Select
							placeholder="Select a Property Type"
							className="add-product-input-pair"
							value={buildingType || undefined}
							onChange={value => setBuildingType(value)}
							options={BUILDING_TYPE_OPTIONS}
							allowClear
							disabled={isLoading}
							suffixIcon={
								<img
									src={process.env.PUBLIC_URL + "/assets/images/icons/black-chevron-down.svg"}
									alt="dropdown icon"
									className="chevron-button"
								/>
							}
						/>
					</div>
				</Form.Item>

				<Form.Item className="login-form-item" validateTrigger="onBlur">
					<div className="property-form-field-with-description">
						<span className="property-form-field-label">{"Occupancy Class (optional)"}</span>
						<Select
							placeholder="Select an Occupancy Class"
							className="add-product-input-pair"
							value={occupancyClass || undefined}
							onChange={value => setOccupancyClass(value)}
							options={OCCUPANCY_CLASS_OPTIONS}
							allowClear
							disabled={isLoading}
							suffixIcon={
								<img
									src={process.env.PUBLIC_URL + "/assets/images/icons/black-chevron-down.svg"}
									alt="dropdown icon"
									className="chevron-button"
								/>
							}
						/>
					</div>
				</Form.Item>

				<Form.Item className="login-form-item" validateTrigger="onBlur">
					<div className="property-form-field-with-description">
						<span className="property-form-field-label">{"Number of Stories (optional)"}</span>
						<Select
							placeholder="Number of Stories"
							className="add-product-input-pair"
							value={numberOfStories || undefined}
							onChange={value => setNumberOfStories(value)}
							options={NUMBER_OF_STORIES_OPTIONS}
							allowClear
							disabled={isLoading}
							suffixIcon={
								<img
									src={process.env.PUBLIC_URL + "/assets/images/icons/black-chevron-down.svg"}
									alt="dropdown icon"
									className="chevron-button"
								/>
							}
						/>
					</div>
				</Form.Item>

				<Form.Item className="login-form-item" validateTrigger="onBlur">
					<div className="property-form-field-with-description">
						<span className="property-form-field-label">{"Number of Units (optional)"}</span>
						<Select
							placeholder="Number of Units"
							className="add-product-input-pair"
							value={numberOfUnits || undefined}
							onChange={value => setNumberOfUnits(value)}
							options={NUMBER_OF_UNITS_OPTIONS}
							allowClear
							disabled={isLoading}
							suffixIcon={
								<img
									src={process.env.PUBLIC_URL + "/assets/images/icons/black-chevron-down.svg"}
									alt="dropdown icon"
									className="chevron-button"
								/>
							}
						/>
					</div>
				</Form.Item>

				<Form.Item className="login-form-item" validateTrigger="onBlur">
					<div className="property-form-field-with-description">
						<span className="property-form-field-label">{"Fire Suppression System (optional)"}</span>
						<Select
							placeholder="Select System Type"
							className="add-product-input-pair"
							value={fireSuppression || undefined}
							onChange={value => setFireSuppression(value)}
							options={FIRE_SUPPRESSION_OPTIONS}
							allowClear
							disabled={isLoading}
							suffixIcon={
								<img
									src={process.env.PUBLIC_URL + "/assets/images/icons/black-chevron-down.svg"}
									alt="dropdown icon"
									className="chevron-button"
								/>
							}
						/>
					</div>
				</Form.Item>

				<Form.Item className="login-form-item" validateTrigger="onBlur">
					<div className="property-form-field-with-description">
						<span className="property-form-field-label">{"Year Built (optional)"}</span>
						<Select
							placeholder="Select Year"
							className="add-product-input-pair"
							value={yearBuilt || undefined}
							onChange={value => setYearBuilt(value)}
							options={YEAR_BUILT_OPTIONS}
							allowClear
							disabled={isLoading}
							suffixIcon={
								<img
									src={process.env.PUBLIC_URL + "/assets/images/icons/black-chevron-down.svg"}
									alt="dropdown icon"
									className="chevron-button"
								/>
							}
						/>
					</div>
				</Form.Item>

				<Form.Item className="login-form-item" validateTrigger="onBlur">
					<div className="property-form-field-with-description">
						<span className="property-form-field-label">{"Device Types (optional)"}</span>
						<span className="property-form-field-description">
							{
								"These are used when analyzing building documentation to identify relevant devices. Press 'Enter' after each device type."
							}
						</span>
						<Select
							mode="tags"
							placeholder="Enter Expected Device Types"
							className="add-product-input-pair"
							value={expectedDeviceTypes}
							onChange={(values: string[]) => setExpectedDeviceTypes(values)}
							allowClear
							disabled={isLoading}
							tokenSeparators={[","]}
						/>
					</div>
				</Form.Item>

				{/* See MWA-238 */}
				{/* <div className="organization-button-container">
          <span className="switch-label">Premium</span>
          <Switch checked={isPremium} onChange={setIsPremium} disabled={isLoading} />
        </div> */}

				<div className="organization-button-container">
					<div className="property-form-field-with-description">
						<span className="property-form-field-label">{"Property Image"}</span>
						<Button
							className="upload-product-button"
							onClick={() => setAddPropertyPhotoModalOpen(true)}
							disabled={isLoading}
						>
							Upload Property Image
						</Button>
					</div>
				</div>

				{propertyPhotoThumbUrl ? (
					<div className="organization-button-container">
						<Image width={170} src={propertyPhotoThumbUrl} />
						<img
							alt="remove-organization-photo-icon"
							src={`${process.env.PUBLIC_URL}/assets/images/icons/remove-trash.svg`}
							className="organization-photo-action-icon"
							onClick={() => {
								if (!isLoading) handleOnRemovePhoto();
							}}
						/>
					</div>
				) : null}

				<UploadPhotoModal
					open={addPropertyPhotoModalOpen}
					onCancel={() => setAddPropertyPhotoModalOpen(false)}
					uploadMethod={handleOnConfirmPhoto}
					disableCrop
				/>
			</Form>

			{/* Primary Contact */}
			<Form className="add-product-form property-form" autoComplete="off">
				<span className="property-form-section-title">{"Primary Contact"}</span>

				<Form.Item className="login-form-item">
					<div className="property-form-field-with-description">
						<span className="property-form-field-label">{"Name (optional)"}</span>
						<span className="property-form-field-description">
							{"First and last name of the property's primary contact. Typically a property manager or owner."}
						</span>
						<Input
							name="contactName"
							placeholder="Primary Contact Name"
							className="add-product-input-pair"
							value={contactName}
							autoComplete="off"
							onChange={e => setContactName(e.target.value)}
							allowClear
							disabled={isLoading}
						/>
					</div>
				</Form.Item>

				<Form.Item className="login-form-item">
					<div className="property-form-field-with-description">
						<span className="property-form-field-label">{"Phone Number (optional)"}</span>
						<Input
							name="contactPhoneNumber"
							placeholder="Phone Number"
							className="add-product-input-pair"
							value={contactPhoneNumber}
							autoComplete="off"
							onChange={e => setContactPhoneNumber(e.target.value)}
							allowClear
							disabled={isLoading}
						/>
					</div>
				</Form.Item>

				<Form.Item className="login-form-item">
					<div className="property-form-field-with-description">
						<span className="property-form-field-label">{"Email (optional)"}</span>
						<Input
							name="contactEmail"
							placeholder="Email Address"
							className="add-product-input-pair"
							value={contactEmail}
							autoComplete="off"
							onChange={e => setContactEmail(e.target.value)}
							allowClear
							disabled={isLoading}
						/>
					</div>
				</Form.Item>
			</Form>

			{/* Plans and Documentation */}
			<div className="add-product-form">
				<span className="add-product-form-title">{"Plans & Documentation"}</span>
				<p className="property-form-field-description">
					{"Note: plans that are part of a submittal package will be uploaded separately during Plan Review."}
				</p>

				{/* Drawings — hidden, see SHOW_DRAWINGS_SECTION */}
				{SHOW_DRAWINGS_SECTION && (
					<>
						<span className="property-form-field-label">{"Drawings (optional)"}</span>
						<p className="property-form-field-description">
							{"Upload relevant design, construction, or as-built drawings."}
						</p>
						<Button className="upload-product-button" onClick={() => setDrawingsModalOpen(true)} disabled={isLoading}>
							Upload Drawings
						</Button>

						{drawingsFiles.length > 0 ? (
							<div className="attachments-container">
								{drawingsFiles.map(documentFile => {
									const uid = documentFile.uid || documentFile.id;
									return (
										<AttachmentItem
											key={`property-drawing-item-${uid}`}
											name={documentFile.name}
											file={documentFile.file}
											type={documentFile.type}
											onRemove={() => {
												if (!isLoading) handleOnRemoveDrawing(documentFile);
											}}
											showEditButton={false}
											onEdit={() => undefined}
										/>
									);
								})}
							</div>
						) : null}
					</>
				)}

				{/* Plans — feed the Ollie room extraction */}
				<span className="property-form-field-label">{"Plans (optional)"}</span>
				<p className="property-form-field-description">
					{
						"Upload property plans (PDF or image). Our AI will extract a building/floor/room structure for tagging evidence during inspection."
					}
				</p>
				<Button className="upload-product-button" onClick={() => setFloorPlansModalOpen(true)} disabled={isLoading}>
					Add Plans
				</Button>

				{floorPlanFiles.length > 0 ? (
					<div className="attachments-container">
						{floorPlanFiles.map(documentFile => {
							const uid = documentFile.uid || documentFile.id;
							return (
								<AttachmentItem
									key={`property-floor-plan-item-${uid}`}
									name={documentFile.name}
									file={documentFile.file}
									type={documentFile.type}
									onRemove={() => {
										if (!isLoading) handleOnRemoveFloorPlan(documentFile);
									}}
									showEditButton={false}
									onEdit={() => undefined}
								/>
							);
						})}
					</div>
				) : null}

				{/* Property Resources */}
				<span className="property-form-field-label">{"Property Resources (optional)"}</span>
				<p className="property-form-field-description">
					{
						"Upload other reference documentation for this property, such as close-out packages, as-builts, and walk-through videos."
					}
				</p>
				<Button className="upload-product-button" onClick={() => setAddDocumentModalOpen(true)} disabled={isLoading}>
					Upload Resources
				</Button>

				{documentsFiles.length > 0 ? (
					<div className="attachments-container">
						{documentsFiles.map(documentFile => {
							const uid = documentFile.uid || documentFile.id;
							return (
								<AttachmentItem
									key={`property-document-item-${uid}`}
									name={documentFile.name}
									file={documentFile.file}
									type={documentFile.type}
									onRemove={() => {
										if (!isLoading) handleOnRemoveDocument(documentFile);
									}}
									showEditButton
									onEdit={() => {
										if (!isLoading) handleOnEditDocument(documentFile);
									}}
								/>
							);
						})}
					</div>
				) : null}

				{/* Warranty */}
				<span className="property-form-field-label">{"Warranty (optional)"}</span>
				<div className="property-add-warranty-container">
					<Button
						className="property-add-warranty-button"
						onClick={() => {
							setWarrantyDocMode(true);
							setAddDocumentModalOpen(true);
						}}
						disabled={isLoading}
					>
						Upload Warranty Document
					</Button>

					<DatePicker
						allowClear
						className="property-add-warranty-date"
						format={"MM/DD/YYYY"}
						placeholder={"Select Start Date"}
						value={warrantyDateStart}
						onChange={date => setWarrantyDateStart(date || null)}
						disabled={isLoading}
					/>

					<DatePicker
						allowClear
						className="property-add-warranty-date"
						format={"MM/DD/YYYY"}
						placeholder={"Select Expiration Date"}
						value={warrantyDateEnd}
						onChange={date => setWarrantyDateEnd(date || null)}
						disabled={isLoading}
					/>
				</div>

				{warrantyDocFile ? (
					<div className="attachments-container">
						<AttachmentItem
							key={`property-document-item-${warrantyDocFile.uid || warrantyDocFile.id}`}
							name={warrantyDocFile.name}
							file={warrantyDocFile.file}
							type={warrantyDocFile.type}
							onRemove={() => {
								if (!isLoading) setWarrantyDocFile(null);
							}}
							showEditButton={false}
							onEdit={() => undefined}
						/>
					</div>
				) : null}
			</div>

			{/* Property Users */}
			<div className="property-form-section">
				<span className="property-form-section-title">{"Access"}</span>

				<div className="property-form-field-with-description">
					<span className="property-form-field-label">{"Users"}</span>
					<span className="property-form-field-description">
						{"Who should have access to view this property? Additional permissions are configured separately."}
					</span>
				</div>

				<Dropdown
					menu={{
						items: usersOptions,
						selectable: true,
						multiple: true,
						onSelect: handleOnSelectUsers,
						onDeselect: handleOnSelectUsers,
						selectedKeys: selectedUsersKeys
					}}
					arrow
					className="organization-properties-dropdown"
					overlayClassName="products-dropdown-overlay"
					trigger={["click"]}
					open={usersDropdownOpen}
					onOpenChange={(open, info) => {
						if (info.source === "trigger" && !open) setUsersDropdownOpen(false);
					}}
				>
					<Button onClick={() => setUsersDropdownOpen(prev => !prev)} disabled={isLoading}>
						Select Users
						<img
							src={process.env.PUBLIC_URL + "/assets/images/icons/black-chevron-down.svg"}
							style={{ width: "1em", height: "1em" }}
							title="Select Users"
						/>
					</Button>
				</Dropdown>

				<div className="organization-selected-properties-container">
					{selectedUsers.map(user => (
						<div key={user.id} className="organization-selected-property">
							<span className="organization-selected-property-name">{`${user.first_name} ${user.last_name}`}</span>
							<span
								className="organization-selected-property-delete"
								onClick={() => {
									if (!isLoading) handleOnRemoveUser(user.id);
								}}
							>
								{"X"}
							</span>
						</div>
					))}
				</div>
			</div>

			{/* Actions */}
			<div className="add-product-button-container">
				{renderExtraActions?.()}
				{/* <Button className="admin-add-product-button" htmlType="submit" onClick={handleOnSubmit} disabled={isLoading}>
					{submitLabel}
				</Button> */}

				<button
					type="submit"
					className="ha-modal-save-btn"
					onClick={handleOnSubmit}
					disabled={
						isLoading ||
						!(lockedOrganizationId || selectedOrganizationId) ||
						!name ||
						!address
					}
				>
					{submitLabel}
				</button>
			</div>

			<AddAttachmentModal
				open={drawingsModalOpen}
				onCancel={() => setDrawingsModalOpen(false)}
				updateAttachments={updateDrawings}
				editMode={false}
				editProduct={false}
				propertyDocMode={true}
				warrantyDocMode={false}
				fixedCategory={PropertyDocumentsType.DRAWING}
			/>

			<AddAttachmentModal
				open={floorPlansModalOpen}
				onCancel={() => setFloorPlansModalOpen(false)}
				updateAttachments={updateFloorPlans}
				editMode={false}
				editProduct={false}
				propertyDocMode={true}
				warrantyDocMode={false}
				fixedCategory={PropertyDocumentsType.FLOOR_PLAN}
			/>

			<AddAttachmentModal
				open={addDocumentModalOpen}
				onCancel={handleOnCloseDocumentModal}
				updateAttachments={updateAttachments}
				editMode={addDocumentModalEditMode}
				editProduct={false}
				propertyDocMode={true}
				warrantyDocMode={warrantyDocMode}
			/>
		</div>
	);
};

export default PropertyForm;
